/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PageHeaderComponent } from '@alfresco/aca-shared';
import { TranslatePipe } from '@ngx-translate/core';
import { FilesComponent } from '../files/files.component';

/**
 * คลังเอกสาร (repository).
 *
 * Wraps ACA's own file list rather than replacing it: `<aca-files>` is rendered
 * untouched, so the context menu, Create and Upload, the column chooser,
 * sorting, selection, drag-and-drop upload and pagination remain ADF's and
 * behave exactly as they do everywhere else. Only the surrounding chrome — the
 * page header and the list toolbar — is added here, to match เอกสารทั้งหมด.
 * The repository-wide summary that used to head this screen was removed: the
 * same five figures already head เอกสารทั้งหมด, and they were never scoped to
 * the folder being browsed, so on any folder below the root they described
 * something other than what was on screen.
 */
@Component({
  imports: [CommonModule, FormsModule, MatIconModule, MatButtonModule, MatTooltipModule, PageHeaderComponent, FilesComponent, TranslatePipe],
  templateUrl: './repository-view.component.html',
  styleUrls: ['./repository-view.component.scss'],
  encapsulation: ViewEncapsulation.None,
  selector: 'aca-repository-view'
})
export class RepositoryViewComponent implements AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild(FilesComponent) private readonly files?: FilesComponent;

  /** Range line on the left of the toolbar, mirroring เอกสารทั้งหมด. */
  readonly range = signal<{ start: number; end: number; total: number | null } | null>(null);

  private readonly host = inject(ElementRef<HTMLElement>);

  private readonly changeDetector = inject(ChangeDetectorRef);

  /**
   * Ordering options, read from the table's own sortable column headers.
   *
   * Building the list from the live header rather than from a hand-written map
   * means it can only ever offer orderings the table actually supports, and the
   * labels are the column titles the user is already looking at.
   */
  sortOptions: { key: string; label: string }[] = [];

  viewMode: 'list' | 'grid' = 'list';

  sortKey = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  ngAfterViewInit(): void {
    this.placeListBar();
    this.watchToolbar();
    this.followPagination();
    // the list loads asynchronously; read its header once it is there
    this.readSorting(600);
  }

  /**
   * Handles a change of ordering column.
   *
   * The select is bound one-way on purpose: with `[(ngModel)]` the two-way
   * handler and an explicit `(ngModelChange)` both run, and the explicit one saw
   * the previous key — so it sorted by the old column and the follow-up
   * direction check clicked it straight back, leaving the control looking inert.
   */
  onSortKeyChange(key: string): void {
    this.sortKey = key;
    this.applySorting();
  }

  /**
   * Applies an ordering by driving ADF's own column header.
   *
   * Setting `documentList.sorting` and reloading does not re-order the list —
   * the request is built from the sorting the data adapter already holds — so a
   * control written that way looks alive and does nothing. Clicking the header
   * is the path a user takes, which is why the two can never disagree.
   *
   * Exactly one click is issued per interaction. An earlier version clicked
   * again until the direction matched; each click starts a reload, and the
   * overlapping requests finished out of order and left the list on whichever
   * one answered last, so the control appeared inert.
   */
  applySorting(): void {
    this.headerTrigger(this.sortKey)?.click();
    this.syncFromHeader();
  }

  /** Flips direction the way a second click on the sorted column does. */
  toggleSortDirection(): void {
    this.headerTrigger(this.sortKey)?.click();
    this.syncFromHeader();
  }

  /** Reads back what ADF actually did, so the control never claims otherwise. */
  private syncFromHeader(): void {
    setTimeout(() => {
      const cell = this.headerCell(this.sortKey);
      const direction = cell ? this.sortedDirection(cell) : null;
      if (direction) {
        this.sortDirection = direction;
      }
      this.changeDetector.detectChanges();
    }, 700);
  }

  /**
   * The element that actually sorts when clicked.
   *
   * The sort handler sits on the header cell's inner content element, not on
   * the cell — a click on the cell bubbles nowhere and does nothing, however
   * complete the event sequence is.
   */
  private headerTrigger(key: string): HTMLElement | null {
    if (!this.headerCell(key)) {
      return null;
    }
    const root = this.host.nativeElement as HTMLElement;
    return root.querySelector<HTMLElement>(`.adf-datatable-header [data-automation-id="auto_header_content_id_${key}"]`);
  }

  private headerCell(key: string): HTMLElement | null {
    const root = this.host.nativeElement as HTMLElement;
    const cell = root.querySelector<HTMLElement>(`.adf-datatable-header [data-automation-id="auto_id_${key}"]`);
    return cell?.classList.contains('adf-sortable') ? cell : null;
  }

  private sortedDirection(cell: HTMLElement): 'asc' | 'desc' | null {
    if (cell.classList.contains('adf-datatable__header--sorted-asc')) {
      return 'asc';
    }
    return cell.classList.contains('adf-datatable__header--sorted-desc') ? 'desc' : null;
  }

  /** Reads the sortable columns and the ordering the table is already using. */
  private readSorting(delay: number): void {
    setTimeout(() => {
      const root = this.host.nativeElement as HTMLElement;
      const cells = Array.from(root.querySelectorAll<HTMLElement>('.adf-datatable-header .adf-sortable[data-automation-id^="auto_id_"]'));
      if (!cells.length) {
        return;
      }
      this.sortOptions = cells.map((cell) => ({
        key: (cell.getAttribute('data-automation-id') ?? '').replace('auto_id_', ''),
        label: (cell.textContent ?? '').replace(/\s+/g, ' ').trim()
      }));
      const active = cells.find((cell) => this.sortedDirection(cell));
      if (active) {
        this.sortKey = (active.getAttribute('data-automation-id') ?? '').replace('auto_id_', '');
        this.sortDirection = this.sortedDirection(active) ?? 'asc';
      } else {
        this.sortKey = this.sortOptions[0].key;
      }
      this.changeDetector.detectChanges();
    }, delay);
  }

  /**
   * Moves the list toolbar below ACA's action row.
   *
   * The action row (breadcrumb, Create, Upload) is rendered by `<aca-files>`,
   * so the toolbar cannot be authored between it and the table from here. The
   * element being moved is this component's own static markup, and it is placed
   * after `.aca-content-header` — a container ACA does not re-render — so the
   * move survives everything the screen does, including navigating folders.
   */
  private placeListBar(): void {
    const root = this.host.nativeElement as HTMLElement;
    const bar = root.querySelector('.aca-repo__listbar');
    const content = root.querySelector('.aca-main-content');
    if (bar && content && bar.parentElement !== content) {
      content.insertBefore(bar, content.firstChild);
    }
    this.placeToolbar();
  }

  /**
   * Moves ACA's Create / Upload toolbar into the list toolbar.
   *
   * The buttons keep working because the element that carries them is moved,
   * not rebuilt — the component and its extension-driven actions are untouched.
   *
   * It has to be watched, though: the toolbar lives inside the `*ngIf` that
   * swaps the header for the Search AI input, so ACA can destroy and recreate it
   * at any time. The observer puts each new one back where it belongs, which is
   * why the buttons cannot silently return to the header row.
   */
  private placeToolbar(): void {
    const root = this.host.nativeElement as HTMLElement;
    const slot = root.querySelector('.aca-repo__listbarleft');
    const toolbar = root.querySelector('aca-toolbar');
    if (slot && toolbar && toolbar.parentElement !== slot) {
      slot.insertBefore(toolbar, slot.firstChild);
    }
  }

  private watchToolbar(): void {
    const root = this.host.nativeElement as HTMLElement;
    const observer = new MutationObserver(() => this.placeListBar());
    observer.observe(root, { childList: true, subtree: true });
    this.destroyRef.onDestroy(() => observer.disconnect());
  }

  /**
   * Mirrors the list's own paging into the range line.
   *
   * The numbers come from the document list's `pagination` subject — the same
   * one its pager renders — so the two can never disagree.
   */
  private followPagination(): void {
    const list = this.files?.documentList;
    if (!list) {
      return;
    }
    list.pagination.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((paging) => {
      const skip = paging?.skipCount ?? 0;
      const count = paging?.count ?? 0;
      this.range.set({
        start: count ? skip + 1 : 0,
        end: skip + count,
        total: paging?.hasMoreItems ? null : (paging?.totalItems ?? skip + count)
      });
      this.changeDetector.detectChanges();
    });
  }

  /**
   * Switches between the row list and a card layout.
   *
   * This is a layout change only: the rows stay ADF's `adf-datatable-row`
   * elements, so the context menu, selection and double-click navigation are
   * untouched. ADF 8.5 has no display mode of its own — neither the document
   * list nor the data table takes one — so the alternative would have been
   * rendering the cards ourselves, and the context-menu directive resolves its
   * target from data table rows, which would have cost the right-click actions.
   */
  setViewMode(mode: 'list' | 'grid'): void {
    this.viewMode = mode;
  }

  /**
   * Opens ADF's own column chooser.
   *
   * The chooser is the data table's, mounted in its header row; this forwards
   * the click to that control rather than reimplementing it, so what opens is
   * the real thing — the same panel, the same saved column preferences.
   */
  openColumnChooser(): void {
    const button = (this.host.nativeElement as HTMLElement).querySelector<HTMLElement>('[data-automation-id="adf-datatable-main-menu-button"]');
    button?.click();
  }
}
